import {
  fetchBootcampByKey,
  fetchBootcampTasksByKey,
  fetchEnrolledBootcampsByGameKey,
  getActiveBootcampsProgress,
  logModuleAttempt,
  logModuleCompletion,
} from "./bootcamp.apis"
import { getChaptersByCourseId } from "@/api/course"
import router from "@/router"

import {
  BOOTCAMP_MODULE_TYPES,
  BOOTCAMP_STEPS,
  BOOTCAMP_TABS,
} from "./bootcamp.constants"
import {
  calculateDays,
  constructModuleQueue,
  getModuleById,
  getTaskBySegmentId,
  isModuleActive,
  isModuleCompleted,
  isQuestion,
} from "./bootcamp.utils"

const state = {
  currentTab: null,
  bootcampStep: BOOTCAMP_STEPS.PANEL, // apply this on small screen
  bootcampMap: {}, // by shortcode
  bootcampModuleQueue: [],
  bootcampQueueIndex: 0,
  lastQueueIndex: 0, // used to remember user's position when clicking curriculum tab
  bootcampProgressMap: {},
  bootcampTasks: [],
  enrolledBootcampsMap: {},
  panelShowing: true,
}
const getters = {
  currentTab: state => state.currentTab,
  bootcampStep: state => state.bootcampStep,
  currentBootcamp: state => {
    const bootcampKey = router.currentRoute.params.key
    return state.bootcampMap[bootcampKey]
  },
  bootcampModuleQueue: state => state.bootcampModuleQueue,
  bootcampQueueIndex: state => state.bootcampQueueIndex,
  lastQueueIndex: state => state.lastQueueIndex,
  chapters: (state, getters) => {
    return getters.currentBootcamp ? getters.currentBootcamp.chapters : []
  },
  currentModule: state => state.bootcampModuleQueue[state.bootcampQueueIndex],
  currentChapter: (state, getters) => {
    if (!getters.currentModule) return null
    const chapters = getters.chapters || []
    const chapter = chapters.find(
      chapter => chapter.id === getters.currentModule.chapterId
    )
    return chapter
  },
  modules: (state, getters) => {
    return getters.currentChapter ? getters.currentChapter.segments : []
  },
  hasPrevModule: (state, getters) => {
    if (
      getters.bootcampQueueIndex <= 0 ||
      !getters.bootcampModuleQueue.length
    ) {
      return null
    }
    const prevIndex = getters.bootcampQueueIndex - 1
    const prevModule = getters.bootcampModuleQueue[prevIndex] || null
    return prevModule !== null && getters.isModuleAccessible(prevModule)
  },
  hasNextModule: (state, getters) => {
    if (
      getters.bootcampQueueIndex >= getters.bootcampModuleQueue.length - 1 ||
      !getters.bootcampModuleQueue.length
    ) {
      return null
    }
    const nextIndex = getters.bootcampQueueIndex + 1
    const nextModule = getters.bootcampModuleQueue[nextIndex] || null
    return nextModule !== null && getters.isModuleAccessible(nextModule)
  },
  isModuleAccessible: (state, getters) => module => {
    const isContentTimeLocked = module.accessible_at &&
      getters.getCalculatedDays(module.accessible_at).days > 0
    return !isContentTimeLocked
  },
  isModuleActive: (state, getters) => module => {
    return isModuleActive(getters.currentModule, module)
  },
  bootcampProgress: (state, getters) => {
    return getters.currentBootcamp
      ? state.bootcampProgressMap[getters.currentBootcamp.id]
      : null
  },
  currentChapterProgress: (state, getters) => {
    return getters.bootcampProgress && getters.currentChapter
      ? getters.bootcampProgress.chapter_progress[getters.currentChapter.id]
      : null
  },
  segmentsProgress: (state, getters) => {
    return getters.currentChapterProgress
      ? getters.currentChapterProgress.segment_progress
      : null
  },
  getCalculatedDays: () => date => {
    return calculateDays(date)
  },
  isQuestion: () => module => isQuestion(module),
  isModuleCompleted: (state, getters) => module => {
    if (!getters.bootcampProgress) {
      return false
    }
    return isModuleCompleted(
      module,
      getters.bootcampProgress.chapter_progress[module.chapterId]
        .segment_progress
    )
  },
  bootcampTasks: state => state.bootcampTasks,
  moduleById: state => id => getModuleById(id, state.bootcampModuleQueue),
  taskBySegmentId: state => id => getTaskBySegmentId(id, state.bootcampTasks),
  enrolledBootcampByKey: state => key => state.enrolledBootcampsMap[key],
  panelShowing: state => state.panelShowing,
}
const mutations = {
  setBootcampMap: (state, bootcampMap) => (state.bootcampMap = bootcampMap),
  setCurrentTab: (state, tab) => (state.currentTab = tab),
  setChaptersMap: (state, chaptersMap) => (state.chaptersMap = chaptersMap),
  setBootcampStep: (state, step) => (state.bootcampStep = step),
  setBootcampModuleQueue: (state, moduleQueue) =>
    (state.bootcampModuleQueue = moduleQueue),
  setBootcampQueueIndex: (state, index) => (state.bootcampQueueIndex = index),
  setLastQueueIndex: (state, index) => (state.lastQueueIndex = index),
  setBootcampProgressMap: (state, map) => (state.bootcampProgressMap = map),
  setBootcampTasks: (state, tasks) => (state.bootcampTasks = tasks),
  setEnrolledBootcampsMap: (state, bootcampsMap) =>
    (state.enrolledBootcampsMap = bootcampsMap),
  setPanelShowing: (state, showing) => (state.panelShowing = showing),
}
const actions = {
  togglePanelShowing ({ commit, state }) {
    commit("setPanelShowing", !state.panelShowing)
  },
  async fetchBootcampByKey ({ commit, state, dispatch }, key) {
    const bootcampMap = state.bootcampMap
    let bootcamp
    // bootcamp is already exists
    if (bootcampMap[key]) {
      return
    }
    bootcamp = await fetchBootcampByKey(key)
    if (bootcamp.course) {
      bootcamp.course.bootcamp = bootcamp.bootcamp
      bootcampMap[router.currentRoute.params.key] = bootcamp.course
      const chapters = await dispatch("fetchChaptersByBootcamp", bootcamp.course)
      bootcamp.course.chapters = chapters
      const queue = constructModuleQueue(chapters)
      commit("setBootcampModuleQueue", queue)
      commit("setBootcampQueueIndex", 0)
      commit("setBootcampMap", { ...bootcampMap })
    }
  },
  async fetchChaptersByBootcamp ({ commit, dispatch }, bootcamp) {
    const chapters = await getChaptersByCourseId(bootcamp.id)
    return chapters
  },
  // construct module queue to iterate for bootcamp
  updateIndexByModule ({ commit, state }, newModule) {
    const moduleIndex = state.bootcampModuleQueue.findIndex(
      module =>
        module.moduleType === BOOTCAMP_MODULE_TYPES.ENTRY &&
        module.id === newModule.id
    )
    commit("setBootcampQueueIndex", moduleIndex)
  },
  goNextStep ({ commit, state }) {
    commit("setBootcampQueueIndex", state.bootcampQueueIndex + 1)
  },
  goPrevStep ({ commit, state }) {
    commit("setBootcampQueueIndex", state.bootcampQueueIndex - 1)
  },
  async getActiveBootcampsProgress ({ commit }) {
    const data = await getActiveBootcampsProgress()
    await commit("setBootcampProgressMap", { ...data })
  },
  async logModuleAttempt ({ getters, dispatch }, module) {
    await logModuleAttempt(module.id)
    if (!getters.bootcampProgress) {
      dispatch("getActiveBootcampsProgress")
    }
  },
  async logModuleCompletion ({ state, getters, dispatch }, module) {
    const moduleId =
      module === BOOTCAMP_MODULE_TYPES.QUESTION ? module.segment.id : module.id
    await logModuleCompletion(moduleId)
    if (getters.bootcampProgress) {
      const bootcampProgressMap = state.bootcampProgressMap
      const bootcampProgress = bootcampProgressMap[getters.currentBootcamp.id]
      const chapterProgress =
        bootcampProgress.chapter_progress[getters.currentChapter.id]
      const moduleProgress = chapterProgress.segment_progress[moduleId]
      moduleProgress.completion_status = "completed"
    } else {
      dispatch("getActiveBootcampsProgress")
    }
  },
  goCurrentModule ({ commit, getters }) {
    if (!getters.bootcampProgress) return
    const continuePoint = getters.bootcampProgress.continuation_point.segment_id
    const index = getters.bootcampModuleQueue.findIndex(
      module => module.id === continuePoint
    )
    if (index > 0) {
      commit("setBootcampQueueIndex", index - 1)
    } else {
      commit("setBootcampQueueIndex", 0)
    }
  },
  async fetchBootcampTasksByKey ({ commit }, key) {
    const data = await fetchBootcampTasksByKey(key)
    commit("setBootcampTasks", data.tasks)
  },
  async fetchEnrolledBootcampsByGameKey ({ commit, state }, key) {
    const data = await fetchEnrolledBootcampsByGameKey(key)
    const bootcampsMap = state.enrolledBootcampsMap
    data.forEach(eachData => {
      bootcampsMap[eachData.bootcamp.key] = eachData
    })
    commit("setEnrolledBootcampsMap", bootcampsMap)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/bootcamp/bootcamp.module.js